import { ProjectState } from './project-state';

export type AgreementCalculationType = 'PRELIMINARY' | 'OFFICIAL';

export type AgreementAggregateMetricType = 'WEIGHTED_PAIRWISE_KAPPA';

export type AgreementInterpretation =
  | 'NOT_AVAILABLE'
  | 'INSUFFICIENT_DATA'
  | 'NOT_COMPUTABLE'
  | 'WORSE_THAN_CHANCE'
  | 'BELOW_FAIR'
  | 'FAIR'
  | 'MODERATE'
  | 'GOOD_SUBSTANTIAL'
  | 'VERY_GOOD';

export type AgreementNonComputableReason =
  | 'NO_OVERLAPPING_DECISIONS'
  | 'INSUFFICIENT_DATA'
  | 'NO_CATEGORY_VARIATION';

export type AgreementThresholdStatus =
  | 'NOT_COMPUTABLE'
  | 'NO_THRESHOLD'
  | 'MEETS_THRESHOLD'
  | 'BELOW_THRESHOLD';

export interface ScreeningAgreementPair {
  firstReviewerId: number;
  secondReviewerId: number;
  firstReviewerName: string;
  secondReviewerName: string;
  pairedItemCount: number;
  agreementCount: number;
  disagreementCount: number;
  percentAgreement: number | null;
  cohenKappa: number | null;
  interpretation: AgreementInterpretation;
  nonComputableReason: AgreementNonComputableReason | null;
  thresholdStatus: AgreementThresholdStatus;
}

export interface ScreeningAgreementBase {
  calculationType: AgreementCalculationType;
  stage: ProjectState;
  pairedDecisionCount: number;
  agreementCount: number;
  disagreementCount: number;
  missingDecisionCount: number;
  unpairedItemCount: number;
  overallPercentAgreement: number | null;
  aggregateMetricType: AgreementAggregateMetricType;
  weightedPairwiseKappa: number | null;
  interpretation: AgreementInterpretation;
  nonComputableReason: AgreementNonComputableReason | null;
  configuredThreshold: number | null;
  thresholdStatus: AgreementThresholdStatus;
  reviewerPairs: ScreeningAgreementPair[];
}

export interface ScreeningAgreementPreview extends ScreeningAgreementBase {
  calculationType: 'PRELIMINARY';
}

export interface ScreeningAgreementOfficial extends ScreeningAgreementBase {
  calculationType: 'OFFICIAL';
  snapshotVersion: number;
  finalizedAt: string;
  finalizedByUserId: number;
  finalizedByUserName: string;
  calculationAlgorithmVersion: string;
}

export type ScreeningAgreementResult = ScreeningAgreementPreview | ScreeningAgreementOfficial;

export const AGREEMENT_INTERPRETATION_LABELS: Record<AgreementInterpretation, string> = {
  WORSE_THAN_CHANCE: 'Worse than chance',
  BELOW_FAIR: 'Below fair',
  FAIR: 'Fair',
  MODERATE: 'Moderate',
  GOOD_SUBSTANTIAL: 'Good / substantial',
  VERY_GOOD: 'Very good',
  NOT_COMPUTABLE: 'Not computable',
  INSUFFICIENT_DATA: 'Insufficient data',
  NOT_AVAILABLE: 'Not available',
};

export const AGREEMENT_THRESHOLD_STATUS_LABELS: Record<AgreementThresholdStatus, string> = {
  NOT_COMPUTABLE: 'Cannot be calculated',
  NO_THRESHOLD: 'No threshold configured',
  MEETS_THRESHOLD: 'Threshold reached',
  BELOW_THRESHOLD: 'Below threshold',
};

export const AGREEMENT_REASON_LABELS: Record<AgreementNonComputableReason, string> = {
  NO_OVERLAPPING_DECISIONS: 'No overlapping reviewer decisions are available yet.',
  INSUFFICIENT_DATA: "At least two paired decisions are needed for Cohen's kappa.",
  NO_CATEGORY_VARIATION:
    "Cohen's kappa cannot be calculated because all decisions used the same category.",
};

export function agreementInterpretationLabel(value?: AgreementInterpretation | null): string {
  return value ? AGREEMENT_INTERPRETATION_LABELS[value] : 'Not available';
}

export function agreementThresholdStatusLabel(value?: AgreementThresholdStatus | null): string {
  return value ? AGREEMENT_THRESHOLD_STATUS_LABELS[value] : 'Not available';
}

export function agreementReasonLabel(value?: AgreementNonComputableReason | null): string {
  return value ? AGREEMENT_REASON_LABELS[value] : '';
}
